import { inject, Injectable, OnDestroy, signal, type Signal, type WritableSignal } from '@angular/core';
import { Subscription } from 'rxjs';
import { SellerAccountModel, UserModel } from '../data';
import { EVENTS_TYPE, EventsInterface, EventsService } from '../services';
import { AuthService } from './auth.service';

/** Time-to-live for cached account IDs (in milliseconds). */
const CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes

/**
 * Caches seller `accountId` values from auth profile for dashboard queries.
 *
 * Uses a TTL-based strategy: if the cache is older than {@link CACHE_TTL_MS},
 * the next call to {@link ensureAccountIds} will refresh it automatically.
 * Call {@link ensureAccountIds} on navigation to guarantee fresh data.
 */
@Injectable({ providedIn: 'root' })
export class AccountsService implements OnDestroy {
  private readonly authService = inject(AuthService);
  private readonly eventsService = inject(EventsService);
  private readonly accountIdsSignal: WritableSignal<string[]> = signal<string[]>([]);
  private readonly eventsSubscription: Subscription;
  private lastFetchedAt: number | null = null;

  public readonly accountIds: Signal<string[]> = this.accountIdsSignal.asReadonly();

  constructor() {
    this.eventsSubscription = this.eventsService.events.subscribe((event: EventsInterface<unknown>) => this.handleAuthEvent(event));
  }

  public ngOnDestroy(): void {
    this.eventsSubscription.unsubscribe();
  }

  /**
   * Returns cached account IDs if still fresh, otherwise fetches from auth.
   * Intended to be called on route navigation (guard/resolver).
   */
  public async ensureAccountIds(): Promise<string[]> {
    if (this.isCacheFresh()) {
      return this.accountIdsSignal();
    }
    return this.loadAccountIds();
  }

  /**
   * Forces a fresh fetch from auth profile regardless of cache age.
   */
  public async loadAccountIds(): Promise<string[]> {
    const profile: UserModel = await this.authService.profile();
    const accounts: SellerAccountModel[] = profile.accounts ?? [];
    const accountIds: string[] = accounts.map((account: SellerAccountModel) => account.accountId).filter((accountId: string) => accountId.trim().length > 0);
    this.accountIdsSignal.set(accountIds);
    this.lastFetchedAt = Date.now();
    return accountIds;
  }

  public clearAccountIds(): void {
    this.accountIdsSignal.set([]);
    this.lastFetchedAt = null;
  }

  private isCacheFresh(): boolean {
    if (this.lastFetchedAt === null || this.accountIdsSignal().length === 0) {
      return false;
    }
    return Date.now() - this.lastFetchedAt < CACHE_TTL_MS;
  }

  private handleAuthEvent(event: EventsInterface<unknown>): void {
    if (event.action !== EVENTS_TYPE.IS_AUTH || event.payload) {
      return;
    }
    this.clearAccountIds();
  }
}
